import React, { useState, useEffect } from 'react';
import { Zap, Heart } from 'lucide-react';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

const getApiConfig = () => ({
  authToken: localStorage.getItem('astrapeak_auth_token'),
  baseUrl: getBaseUrl()
});

interface Stats {
  total_enrichments: number;
  success_rate: number;
  avg_latency_ms: number;
  uptime_hours: number;
}

interface Health {
  overall_health: string;
}

export const EnrichmentStatus: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { authToken, baseUrl } = getApiConfig();
      const [statsRes, healthRes] = await Promise.all([
        fetch(`${baseUrl}/api/platform-admin/ml/enrichment/stats`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        }),
        fetch(`${baseUrl}/api/platform-admin/ml/enrichment/health`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        })
      ]);
      setStats(await statsRes.json());
      setHealth(await healthRes.json());
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 flex items-center gap-2">
        <Zap className="w-6 h-6" />
        Enrichment Pipeline Status
      </h1>

      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Total</p>
            <p className="text-2xl font-bold">{stats.total_enrichments}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Success Rate</p>
            <p className="text-2xl font-bold text-green-600">{(stats.success_rate * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Avg Latency</p>
            <p className="text-2xl font-bold">{stats.avg_latency_ms.toFixed(0)}ms</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <p className="text-sm text-gray-600">Uptime</p>
            <p className="text-2xl font-bold">{stats.uptime_hours.toFixed(1)}h</p>
          </div>
        </div>
      )}

      {health && (
        <div className="bg-white border rounded-lg p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Heart className="w-5 h-5" />
            Health Status: <span className="text-green-600">{health.overall_health}</span>
          </h3>
        </div>
      )}
    </div>
  );
};
